import { useState, useEffect, useCallback } from "react";
import DiarioCapitale from "./DiarioCapitale";
import DiarioBookmaker from "./DiarioBookmaker";
import DiarioOperazioni from "./DiarioOperazioni";
import DiarioImpostazioni from "./DiarioImpostazioni";
import DiarioStatistiche from "./DiarioStatistiche";

import { API } from "../config";

// ── Stili comuni (coerenti con MonitorLive.js) ──────────────────
const FONT = "'DM Sans', sans-serif";
const MONO = "'IBM Plex Mono', monospace";

const stili = {
  pagina: {
    padding: "24px 32px", fontFamily: FONT,
    background: "#FAFBFC", minHeight: "100vh",
  },
  titolo: {
    fontSize: 22, fontWeight: 700, color: "#1E293B",
    margin: "0 0 4px 0",
  },
  layout: {
    display: "flex", gap: 20, alignItems: "flex-start",
  },
  colonnaSinistra: {
    flex: "0 0 280px",
    position: "sticky", top: 24,
  },
  colonnaDestra: {
    flex: 1, minWidth: 0,
  },
  card: {
    background: "#fff", borderRadius: 10,
    border: "1px solid #E2E8F0",
    padding: "16px 20px", marginBottom: 16,
  },
  // ── Box capitale laterale ───────────────────────────────────
  boxCapitaleTitolo: {
    fontSize: 12, fontWeight: 700, color: "#64748B",
    textTransform: "uppercase", letterSpacing: 0.5,
    marginBottom: 12, paddingBottom: 8,
    borderBottom: "1px solid #E2E8F0",
  },
  boxCapitaleRiga: {
    marginBottom: 14,
  },
  boxCapitaleLabel: {
    fontSize: 11, fontWeight: 600, color: "#64748B",
    marginBottom: 3, fontFamily: FONT,
  },
  boxCapitaleValore: {
    fontSize: 18, fontWeight: 700, color: "#1E293B",
    fontFamily: MONO,
  },
  boxCapitaleValorePiccolo: {
    fontSize: 14, fontWeight: 600, fontFamily: MONO,
  },
  boxCapitaleNota: {
    fontSize: 10, color: "#94A3B8", marginTop: 2,
    fontStyle: "italic",
  },
  // ── Tab ─────────────────────────────────────────────────────
  tabBar: {
    display: "flex", gap: 4, marginBottom: 16,
  },
  tab: {
    fontFamily: FONT, fontSize: 14, fontWeight: 600,
    padding: "10px 18px", border: "none",
    background: "transparent", color: "#64748B",
    cursor: "pointer", borderBottom: "none",
    outline: "none", boxShadow: "none",
    transition: "color 0.15s, box-shadow 0.15s",
  },
  tabAttivo: {
    color: "#2563EB", boxShadow: "inset 0 -3px 0 0 #2563EB",
  },
  // ── Placeholder tab non ancora implementati ─────────────────
  placeholder: {
    background: "#fff", borderRadius: 10,
    border: "1px dashed #CBD5E1", padding: "60px 24px",
    textAlign: "center", color: "#94A3B8",
  },
  placeholderTitolo: {
    fontSize: 16, fontWeight: 600, color: "#64748B",
    marginBottom: 8,
  },
  placeholderTesto: {
    fontSize: 13, color: "#94A3B8",
  },
  // ── Reminder allineamento ───────────────────────────────────
  reminderInfo: {
    fontSize: 11, color: "#94A3B8", fontStyle: "italic",
    paddingTop: 10, borderTop: "1px solid #E2E8F0",
    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
  },
  reminderBannerVicino: {
    background: "#FAEEDA", border: "1px solid #EF9F27",
    borderRadius: 6, padding: "8px 10px",
    marginTop: 12,
    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
  },
  reminderBannerVicinoTesto: {
    fontSize: 11, color: "#854F0B", fontWeight: 600,
  },
  reminderBannerScaduto: {
    background: "#FCEBEB", border: "1px solid #E24B4A",
    borderRadius: 6, padding: "8px 10px",
    marginTop: 12,
  },
  reminderBannerScadutoTesto: {
    fontSize: 11, color: "#791F1F", fontWeight: 600, marginBottom: 4,
    display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
  },
  reminderBannerScadutoSubtesto: {
    fontSize: 10, color: "#A32D2D", lineHeight: 1.4, marginBottom: 8,
  },
  bottoneSalta: {
    background: "#fff", border: "1px solid #E24B4A",
    color: "#791F1F", fontSize: 10, fontWeight: 600,
    padding: "3px 8px", borderRadius: 4,
    cursor: "pointer", fontFamily: FONT,
  },
  // Icona "i" inline
  iconaInfo: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 14, height: 14, borderRadius: "50%",
    background: "#94A3B8", color: "#fff",
    fontSize: 9, fontWeight: 700, fontStyle: "normal",
    cursor: "help", userSelect: "none",
  },
  // ── Popup info reminder ─────────────────────────────────────
  popupOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15, 23, 42, 0.4)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  popupBox: {
    background: "#fff", borderRadius: 10,
    padding: "20px 24px", maxWidth: 520, width: "90vw",
    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
    fontFamily: FONT, color: "#334155", fontSize: 13,
    lineHeight: 1.55, position: "relative",
  },
  popupTitolo: {
    fontSize: 15, fontWeight: 700, color: "#1E293B",
    marginBottom: 12,
  },
  popupChiudiX: {
    position: "absolute", top: 12, right: 14,
    background: "transparent", border: "none",
    color: "#94A3B8", cursor: "pointer",
    fontSize: 18, fontWeight: 700, lineHeight: 1,
    padding: 4,
  },
  popupAzioni: {
    display: "flex", justifyContent: "flex-end",
    gap: 8, marginTop: 18,
  },
  bottonePopupOk: {
    fontFamily: FONT, fontSize: 13, fontWeight: 600,
    padding: "8px 16px", borderRadius: 6,
    border: "none", background: "#2563EB", color: "#fff",
    cursor: "pointer",
  },
  bottonePopupAnnulla: {
    fontFamily: FONT, fontSize: 13, fontWeight: 600,
    padding: "8px 16px", borderRadius: 6,
    border: "1px solid #CBD5E1", background: "#fff",
    color: "#64748B", cursor: "pointer",
  },
};

// ── Formattazione € ─────────────────────────────────────────────
function formatEur(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  const negativo = n < 0;
  const abs = Math.abs(n);
  const intero = Math.trunc(abs);
  const dec = Math.round((abs - intero) * 100);
  const decStr = String(dec).padStart(2, "0");
  const interoStr = String(intero).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return (negativo ? "-" : "") + interoStr + "," + decStr + " €";
}

function formatPct(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const segno = n > 0 ? "+" : "";
  return segno + n.toLocaleString("it-IT", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }) + " %";
}

// ── Componente Box Capitale (incorporato qui) ───────────────────
function BoxCapitaleAttivo({ stato, loading, errore, onSaltato }) {
  const [popupInfoAperto, setPopupInfoAperto] = useState(false);
  const [popupSaltaAperto, setPopupSaltaAperto] = useState(false);
  const [salvataggioSalta, setSalvataggioSalta] = useState(false);

  if (loading) {
    return (
      <div style={stili.card}>
        <div style={stili.boxCapitaleTitolo}>Stagione corrente</div>
        <div style={{ fontSize: 12, color: "#94A3B8" }}>Caricamento...</div>
      </div>
    );
  }
  if (errore) {
    return (
      <div style={stili.card}>
        <div style={stili.boxCapitaleTitolo}>Stagione corrente</div>
        <div style={{ fontSize: 12, color: "#C62828" }}>Errore: {errore}</div>
      </div>
    );
  }
  if (!stato) return null;

  // Stato "nessuna stagione configurata" o capitale_inizio_stagione = 0
  if (!stato.stagione || !stato.capitale_inizio_stagione) {
    return (
      <div style={stili.card}>
        <div style={stili.boxCapitaleTitolo}>Stagione corrente</div>
        <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
          Nessuna stagione configurata.
          <br />
          <span style={{ color: "#94A3B8", fontStyle: "italic" }}>
            Vai sulla tab Impostazioni → Stagioni per configurarla.
          </span>
        </div>
      </div>
    );
  }

  const trend = Number(stato.trend_pct || 0);
  const trendColor = trend > 0 ? "#2E7D32" : trend < 0 ? "#C62828" : "#64748B";
  const etichetta = stato.stagione?.etichetta || "—";

  return (
    <div style={stili.card}>
      <div
        style={stili.boxCapitaleTitolo}
        title="La stagione attualmente in corso, definita in Impostazioni → Stagioni."
      >
        Stagione {etichetta}
      </div>

      <div style={stili.boxCapitaleRiga}>
        <div
          style={stili.boxCapitaleLabel}
          title="Valore statico impostato all'inizio della stagione. Resta invariato per tutta la durata."
        >
          Capitale di inizio stagione
        </div>
        <div style={stili.boxCapitaleValore}>
          {formatEur(stato.capitale_inizio_stagione)}
        </div>
        {stato.stagione?.data_inizio && (
          <div style={stili.boxCapitaleNota}>
            dal {new Date(stato.stagione.data_inizio).toLocaleDateString("it-IT")}
          </div>
        )}
      </div>

      <div style={stili.boxCapitaleRiga}>
        <div
          style={stili.boxCapitaleLabel}
          title="Valore usato per calcolare lo stake delle operazioni in base al Size%."
        >
          Capitale operativo
        </div>
        <div style={stili.boxCapitaleValore}>
          {formatEur(stato.capitale_operativo)}
        </div>
      </div>

      <div style={stili.boxCapitaleRiga}>
        <div
          style={stili.boxCapitaleLabel}
          title="Capitale di inizio stagione + profitti/perdite delle operazioni della stagione."
        >
          Capitale corrente
        </div>
        <div style={stili.boxCapitaleValore}>
          {formatEur(stato.capitale_corrente)}
        </div>
      </div>

      <div style={{ ...stili.boxCapitaleRiga, marginBottom: 0 }}>
        <div
          style={stili.boxCapitaleLabel}
          title="Performance della stagione: variazione del Capitale corrente rispetto al Capitale di inizio stagione."
        >
          Trend stagionale
        </div>
        <div style={{ ...stili.boxCapitaleValorePiccolo, color: trendColor }}>
          {formatPct(trend)}
        </div>
      </div>

      {/* v125: blocco reminder allineamento */}
      <ReminderBlock
        giorniMancanti={stato.reminder_giorni_mancanti}
        frequenzaGiorni={stato.reminder_frequenza_giorni}
        onApriInfo={() => setPopupInfoAperto(true)}
        onApriSalta={() => setPopupSaltaAperto(true)}
      />

      {/* Popup info "Cos'è l'allineamento" */}
      {popupInfoAperto && (
        <div style={stili.popupOverlay} onClick={() => setPopupInfoAperto(false)}>
          <div style={stili.popupBox} onClick={(e) => e.stopPropagation()}>
            <button
              style={stili.popupChiudiX}
              onClick={() => setPopupInfoAperto(false)}
              title="Chiudi"
            >×</button>
            <div style={stili.popupTitolo}>Cos'è l'allineamento del capitale operativo</div>
            <div>
              <p style={{ marginTop: 0 }}>
                Periodicamente è buona pratica adeguare il <strong>Capitale operativo</strong> al <strong>Capitale corrente</strong>. Questo aggiornamento ricalibra le size delle tue future operazioni sul capitale effettivamente disponibile.
              </p>
              <p>
                La frequenza è quella che hai impostato in <strong>Impostazioni → Reminder revisione capitale</strong> (di default ogni 90 giorni).
              </p>
              <p style={{ marginBottom: 0 }}>
                Quando vuoi farlo, vai in <strong>Impostazioni → Stagioni</strong>, modifica la stagione corrente e clicca sul pulsante <strong>"Allinea al corrente"</strong> accanto al campo Capitale operativo.
              </p>
            </div>
            <div style={stili.popupAzioni}>
              <button
                style={stili.bottonePopupOk}
                onClick={() => setPopupInfoAperto(false)}
              >Ho capito</button>
            </div>
          </div>
        </div>
      )}

      {/* Popup conferma "Salta questo allineamento" */}
      {popupSaltaAperto && (
        <div style={stili.popupOverlay} onClick={() => !salvataggioSalta && setPopupSaltaAperto(false)}>
          <div style={stili.popupBox} onClick={(e) => e.stopPropagation()}>
            <button
              style={stili.popupChiudiX}
              onClick={() => setPopupSaltaAperto(false)}
              title="Chiudi"
              disabled={salvataggioSalta}
            >×</button>
            <div style={stili.popupTitolo}>Saltare questo allineamento?</div>
            <div>
              Il prossimo promemoria di allineamento del capitale operativo apparirà tra <strong>{stato.reminder_frequenza_giorni || 90} giorni</strong> (frequenza impostata in Impostazioni → Reminder revisione capitale).
            </div>
            <div style={stili.popupAzioni}>
              <button
                style={stili.bottonePopupAnnulla}
                onClick={() => setPopupSaltaAperto(false)}
                disabled={salvataggioSalta}
              >Annulla</button>
              <button
                style={{
                  ...stili.bottonePopupOk,
                  ...(salvataggioSalta ? { opacity: 0.6, cursor: "wait" } : {}),
                }}
                onClick={async () => {
                  setSalvataggioSalta(true);
                  try {
                    const r = await fetch(`${API}/diario/capitale/conferma-trimestre`, { method: "POST" });
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    setPopupSaltaAperto(false);
                    if (typeof onSaltato === "function") onSaltato();
                  } catch (e) {
                    alert("Errore: " + (e.message || "rete"));
                  } finally {
                    setSalvataggioSalta(false);
                  }
                }}
                disabled={salvataggioSalta}
              >{salvataggioSalta ? "Salvataggio..." : "OK, salta"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sotto-componente reminder (3 stati) ────────────────────────
function ReminderBlock({ giorniMancanti, frequenzaGiorni, onApriInfo, onApriSalta }) {
  if (giorniMancanti === null || giorniMancanti === undefined) return null;
  const g = Number(giorniMancanti);

  const iconaInfo = (
    <span
      style={stili.iconaInfo}
      title="Cos'è l'allineamento?"
      onClick={(e) => { e.stopPropagation(); onApriInfo(); }}
    >i</span>
  );

  // Stato 3: scaduto (giorni mancanti <= 0)
  if (g <= 0) {
    const giorniScaduti = Math.abs(g);
    return (
      <div style={stili.reminderBannerScaduto}>
        <div style={stili.reminderBannerScadutoTesto}>
          ⚠️ Allineamento scaduto {giorniScaduti === 0 ? "oggi" : `da ${giorniScaduti} giorni`}
          {iconaInfo}
        </div>
        <div style={stili.reminderBannerScadutoSubtesto}>
          Vai in Impostazioni → Stagioni per allineare il capitale operativo.
        </div>
        <button
          style={stili.bottoneSalta}
          onClick={onApriSalta}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FEF2F2")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
        >
          Salta questo allineamento
        </button>
      </div>
    );
  }

  // Stato 2: vicino (1-15 giorni)
  if (g <= 15) {
    return (
      <div style={stili.reminderBannerVicino}>
        <span style={stili.reminderBannerVicinoTesto}>
          ⏰ Prossimo allineamento tra {g} {g === 1 ? "giorno" : "giorni"}
        </span>
        {iconaInfo}
      </div>
    );
  }

  // Stato 1: lontano (>15 giorni)
  return (
    <div style={stili.reminderInfo}>
      <span>Prossimo allineamento tra {g} giorni</span>
      {iconaInfo}
    </div>
  );
}

// ── Pagina principale ───────────────────────────────────────────
export default function DiarioOperativo() {
  const [tabAttiva, setTabAttiva] = useState("diario");
  const [statoCapitale, setStatoCapitale] = useState(null);
  const [loadingCap, setLoadingCap] = useState(true);
  const [erroreCap, setErroreCap] = useState(null);

  // Carica stato capitale per il box laterale
  const ricaricaStatoCapitale = useCallback(async () => {
    setLoadingCap(true);
    setErroreCap(null);
    try {
      const r = await fetch(`${API}/diario/capitale/stato`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setStatoCapitale(data);
    } catch (e) {
      setErroreCap(e.message || "Errore di rete");
    } finally {
      setLoadingCap(false);
    }
  }, []);

  useEffect(() => {
    ricaricaStatoCapitale();
  }, [ricaricaStatoCapitale]);

  const tabs = [
    { id: "diario",     label: "📓 Diario" },
    { id: "capitale",   label: "💰 Capitale" },
    { id: "bookmaker",  label: "🏦 Bookmaker" },
    { id: "statistiche", label: "📊 Statistiche" },
    { id: "impostazioni", label: "⚙️ Impostazioni" },
  ];

  return (
    <div style={stili.pagina}>
      <h1 style={stili.titolo}>Diario Operativo</h1>
      <p style={{ fontSize: 13, color: "#64748B", marginBottom: 24 }}>
        Registro delle operazioni eseguite e monitoraggio del rendimento reale.
      </p>

      <div style={stili.layout}>
        {/* Colonna sinistra — box capitale fisso */}
        <div style={stili.colonnaSinistra}>
          <BoxCapitaleAttivo
            stato={statoCapitale}
            loading={loadingCap}
            errore={erroreCap}
            onSaltato={ricaricaStatoCapitale}
          />
        </div>

        {/* Colonna destra — tab */}
        <div style={stili.colonnaDestra}>
          <div style={stili.tabBar}>
            {tabs.map(t => (
              <button
                key={t.id}
                style={{
                  ...stili.tab,
                  ...(tabAttiva === t.id ? stili.tabAttivo : {}),
                }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setTabAttiva(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tabAttiva === "diario" && (
            <DiarioOperazioni onProfittoCambiato={ricaricaStatoCapitale} />
          )}

          {tabAttiva === "capitale" && (
            <DiarioCapitale />
          )}

          {tabAttiva === "bookmaker" && (
            <DiarioBookmaker />
          )}

          {tabAttiva === "statistiche" && (
            <DiarioStatistiche />
          )}

          {tabAttiva === "impostazioni" && (
            <DiarioImpostazioni onCapitaliCambiati={ricaricaStatoCapitale} />
          )}
        </div>
      </div>
    </div>
  );
}
